/**
 * Wake-up scheduling uses PBX-local wall clock (same source as
 * `system_settings.default_time_zone` on the backend). The API expects
 * `wuc_timestamp` as an absolute UTC instant.
 */

type ZonedParts = {
  y: number
  mo: number
  d: number
  h: number
  mi: number
  se: number
}

const readZonedParts = (
  epochMs: number,
  fmt: Intl.DateTimeFormat,
): ZonedParts => {
  const parts = fmt.formatToParts(new Date(epochMs))
  const n = (t: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((p) => p.type === t)?.value ?? NaN)
  return {
    y: n('year'),
    mo: n('month'),
    d: n('day'),
    h: n('hour'),
    mi: n('minute'),
    se: n('second'),
  }
}

const makeZonedFormatter = (timeZone: string) =>
  new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  })

const wallClockMatches = (
  z: ZonedParts,
  y: number,
  mo: number,
  d: number,
  h: number,
  mi: number,
  se: number,
): boolean =>
  z.y === y &&
  z.mo === mo &&
  z.d === d &&
  z.h === h &&
  z.mi === mi &&
  z.se === se

/**
 * Find the UTC instant such that `timeZone`'s local wall clock equals
 * `dateStr` + `timeNormal` (DST-aware). Returns the **earliest** matching
 * instant (so the first 1:30 AM is chosen during a DST "fall back"
 * ambiguous hour). Uses an offset estimate then searches ±2h.
 *
 * Returns `null` if no minute-aligned match exists (e.g. spring-forward gap).
 */
export function findUtcInstantForZonedWallClock(
  dateStr: string,
  timeNormal: string,
  timeZone: string,
): Date | null {
  const [Y, M, D] = dateStr.split('-').map(Number)
  const segs = timeNormal.split(':')
  const h = Number(segs[0])
  const mi = Number(segs[1] ?? 0)
  const se = Number(segs[2] ?? 0)

  const fmt = makeZonedFormatter(timeZone)
  const naiveUtcMs = Date.UTC(Y, M - 1, D, h, mi, se)

  // Estimate the zone's UTC offset by reading what wall clock the naive guess produces,
  // then adjust. This gets us within minutes of the correct answer.
  const probe = readZonedParts(naiveUtcMs, fmt)
  const probeMs = Date.UTC(
    probe.y,
    probe.mo - 1,
    probe.d,
    probe.h,
    probe.mi,
    probe.se,
  )
  const estimatedOffsetMs = probeMs - naiveUtcMs
  const guess = naiveUtcMs - estimatedOffsetMs

  // Search ±2h around the guess (~240 iterations instead of ~2,880).
  const stepMs = 60 * 1000
  const windowMs = 2 * 60 * 60 * 1000

  for (let delta = -windowMs; delta <= windowMs; delta += stepMs) {
    const ms = guess + delta
    const z = readZonedParts(ms, fmt)
    if (wallClockMatches(z, Y, M, D, h, mi, se)) {
      return new Date(ms)
    }
  }

  return null
}

/**
 * Normalize `HH:MM` from the time picker to `HH:MM:SS`.
 */
export function normalizeWakeTimeHms(timeHms: string): string {
  const parts = timeHms.split(':')
  if (parts.length === 2) return `${timeHms}:00`
  return timeHms
}

/**
 * Convert PBX-local calendar date + wall time to a UTC ISO string for
 * `wuc_timestamp` on create/bulk-create.
 */
export function pbxWallTimeToUtcIso(
  dateStr: string,
  timeHms: string,
  timeZone: string,
): string | null {
  const timeNormal = normalizeWakeTimeHms(timeHms)
  const instant = findUtcInstantForZonedWallClock(dateStr, timeNormal, timeZone)
  if (!instant) return null
  return instant.toISOString()
}
